import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useCompany } from "#modules/company/view/useCompany";
import { useUpdateCompanySetting } from "#modules/company/view/useUpdateCompanySetting";
import { PageLayout } from "#shared/layouts/PageLayout";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { Textarea } from "#shared/view/designSystem/inputs/Textarea";

const quoteTemplateSchema = z.object({
  quoteTemplate: z.string().nullable(),
});

type QuoteTemplateFormData = z.infer<typeof quoteTemplateSchema>;

export const QuoteTemplateSettings = () => {
  const { t } = useTranslation();
  const { company } = useCompany();
  const { updateCompanySetting } = useUpdateCompanySetting();

  const form = useForm<QuoteTemplateFormData>({
    defaultValues: {
      quoteTemplate: company?.companySettings.quoteTemplate || null,
    },
  });

  const onSubmit = async (data: QuoteTemplateFormData) => {
    updateCompanySetting({
      quoteTemplate: data.quoteTemplate,
    });
  };

  const { handleSubmit, formState } = form;

  return (
    <PageLayout titleIcon="file" title={t("settings.quoteTemplate.title")} withBorder fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md" w="100%">
          <Textarea
            placeholder={t("settings.quoteTemplate.placeholder")}
            maxRows={20}
            autosize
            styles={{
              input: {
                whiteSpace: "pre-wrap",
              },
            }}
            {...form.register("quoteTemplate")}
          />

          <Button type="submit" loading={formState.isSubmitting}>
            {t("settings.quoteTemplate.save")}
          </Button>
        </Stack>
      </form>
    </PageLayout>
  );
};
